import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, interval, Observable, of } from 'rxjs';
import { catchError, map, share, shareReplay, switchMap } from 'rxjs/operators';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { environment } from 'src/environments/environment';
import type { EstadoTramosUsdtResponse } from './main-rates.service';
import {
  normalizarBinance,
  PrecioP2pUsdtVes,
} from '../components/main-rates/precio-binance-bloque.rules';

/**
 * Lecturas del bloque «Dónde está cada precio» del panel de operadores.
 *
 * Las dos son de fondo (se repiten con el latido del panel), así que nunca
 * lanzan: un fallo devuelve null y el bloque muestra «Binance no disponible»
 * o solo la base. Van con `X-Skip-Error-Interceptor` para que un 403/404 no
 * abra el aviso global encima del panel cada minuto.
 */
@Injectable({ providedIn: 'root' })
export class PrecioBinanceService {
  private apiUrl = environment.apiNest;

  /**
   * Los dos bloques (VES→USDT y USDT→VES) piden lo mismo con el mismo latido:
   * dentro de esta ventana comparten la misma lectura.
   */
  static readonly VENTANA_COMPARTIDA_MS = 20_000;
  private compartidos = new Map<string, { en: number; obs: Observable<any> }>();

  /** Cada cuánto se vuelven a leer Binance y los tramos mientras hay un bloque a la vista. */
  static readonly REFRESCO_MS = 60_000;

  /**
   * Un solo latido para todos los bloques: un único `interval` compartido que
   * existe mientras haya al menos un bloque suscripto (abierto) y se apaga
   * solo con el último (`share` con refCount). Así los dos bloques leen en el
   * mismo instante —y la ventana compartida hace que Binance se pida una sola
   * vez— y nadie consulta con las secciones plegadas.
   */
  private readonly latido$ = interval(PrecioBinanceService.REFRESCO_MS).pipe(share());

  constructor(
    private http: HttpClient,
    private cognitoService: CognitoService,
  ) {}

  private compartido<T>(clave: string, crear: () => Observable<T>): Observable<T> {
    const ahora = Date.now();
    const previo = this.compartidos.get(clave);
    if (previo && ahora - previo.en < PrecioBinanceService.VENTANA_COMPARTIDA_MS) {
      return previo.obs as Observable<T>;
    }
    const obs = crear().pipe(shareReplay({ bufferSize: 1, refCount: false }));
    this.compartidos.set(clave, { en: ahora, obs });
    return obs;
  }

  /** Latido compartido de los bloques (ver `latido$`). */
  latido(): Observable<number> {
    return this.latido$;
  }

  private async headers(): Promise<HttpHeaders> {
    const session = await this.cognitoService.getSession();
    const token = session?.accessToken?.jwtToken?.trim();
    if (!token) throw new Error('No se pudo obtener el token de sesión');
    return new HttpHeaders()
      .set('Authorization', `Bearer ${token}`)
      .set('X-Skip-Error-Interceptor', '1');
  }

  /**
   * Precio P2P USDT/VES en vivo (solo operadores).
   * GET /rates/binance-p2p/usdt-ves. null = Binance no disponible.
   */
  obtenerPrecioUsdtVes(): Observable<PrecioP2pUsdtVes | null> {
    return this.compartido('binance', () =>
      from(this.headers()).pipe(
        switchMap((headers) =>
          this.http.get<PrecioP2pUsdtVes>(`${this.apiUrl}rates/binance-p2p/usdt-ves`, { headers }),
        ),
        map((r) => normalizarBinance(r)),
        catchError(() => of(null)),
      ),
    );
  }

  /**
   * Tramos USDT→VES con la base vigente, misma forma que los de VES→USDT.
   * GET /rates/tramos-usdt/usdt-ves. Mientras el back no lo publique (404) o
   * si falla, null: el bloque se queda con la base.
   */
  obtenerTramosUsdtVes(): Observable<EstadoTramosUsdtResponse | null> {
    return this.compartido('tramos-usdt-ves', () =>
      from(this.headers()).pipe(
        switchMap((headers) =>
          this.http.get<EstadoTramosUsdtResponse>(`${this.apiUrl}rates/tramos-usdt/usdt-ves`, { headers }),
        ),
        map((r) => (r && Array.isArray(r.tramos) ? r : null)),
        catchError(() => of(null)),
      ),
    );
  }

  /**
   * Tramos VES→USDT tal como los ve el cliente (estado). Igual que
   * `MainRatesService.obtenerTramosVesUsdt` pero sin aviso global y sin lanzar.
   * GET /rates/tramos-usdt/ves-usdt
   */
  obtenerTramosVesUsdt(): Observable<EstadoTramosUsdtResponse | null> {
    return this.compartido('tramos-ves-usdt', () =>
      from(this.headers()).pipe(
        switchMap((headers) =>
          this.http.get<EstadoTramosUsdtResponse>(`${this.apiUrl}rates/tramos-usdt/ves-usdt`, { headers }),
        ),
        map((r) => (r && Array.isArray(r.tramos) ? r : null)),
        catchError(() => of(null)),
      ),
    );
  }
}
